export interface FareRuleListHeader {
  header_name: string;
  head?: string;
  value?: string;
  template: string;
}

export interface FormattedFareRuleData {
  isLoader?: boolean;
  success?: boolean;
  message?: string;
  list_header?: FareRuleListHeader[];
  list_body?: Record<string, any[]> | any[];
  arrayData?: any[];
  fare_rule?: any;
  body?: Record<string, any> | any[];
  html?: any;
  rules?: any[];
  additionalInfo?: any;
  activeFareRuleTab?: string;
  data?: any;
}

export interface StaticFareRuleDisplay {
  body: Record<string, any>[];
  header: { label: string; template: string }[];
}

export type EmbeddedFareRuleResult =
  | { kind: 'component'; data: FormattedFareRuleData }
  | { kind: 'static'; data: StaticFareRuleDisplay }
  | null;

export const DEFAULT_FARE_RULE_ERROR_MESSAGE =
  'Fare rule not found. Please contact admin';

export const DEFAULT_LIST_HEADER: FareRuleListHeader[] = [
  {
    header_name: 'Cancellation',
    head: 'cancellation_fee',
    value: 'cancellation_fee_val',
    template: 'cancellation',
  },
  {
    header_name: 'Date change',
    head: 'data_change_fee',
    value: 'data_change_fee_val',
    template: 'dateChange',
  },
  {
    header_name: 'No Show',
    head: 'no_show_fee',
    value: 'no_show_fee_val',
    template: 'noShow',
  },
  {
    header_name: 'Seat Chargeable',
    head: 'seat_chargeable_fee',
    value: 'seat_chargeable_fee_val',
    template: 'seatChange',
  },
  {
    header_name: 'Baggage',
    head: 'baggage_fee',
    value: 'baggage_fee_val',
    template: 'baggage',
  },
];

function getFareRuleErrorMessage(raw: any): string | null {
  if (!raw || typeof raw !== 'object') return null;

  if (raw.type === 'error') {
    return raw.message || DEFAULT_FARE_RULE_ERROR_MESSAGE;
  }

  if (raw.success === false) {
    return raw.message || raw.error || DEFAULT_FARE_RULE_ERROR_MESSAGE;
  }

  if (raw.status?.success === false) {
    return raw.status.message || raw.message || DEFAULT_FARE_RULE_ERROR_MESSAGE;
  }

  if (raw.data?.success === false) {
    return raw.data.message || raw.message || DEFAULT_FARE_RULE_ERROR_MESSAGE;
  }

  if (raw.data?.status?.success === false) {
    return raw.data.status.message || raw.message || DEFAULT_FARE_RULE_ERROR_MESSAGE;
  }

  return null;
}

function isEmptyFareRulePayload(raw: any): boolean {
  if (raw == null) return true;
  if (typeof raw === 'string') return raw.trim().length === 0;
  if (Array.isArray(raw)) return raw.length === 0;
  if (typeof raw === 'object') return Object.keys(raw).length === 0;
  return false;
}

function normalizeFareRuleEnvelope(raw: any): any {
  if (raw == null || typeof raw === 'string' || Array.isArray(raw)) return raw;
  if (typeof raw !== 'object') return raw;

  if (raw.fareRuleData !== undefined) return raw.fareRuleData;

  if (raw.data !== undefined) {
    if (raw.data?.fareRuleData !== undefined) return raw.data.fareRuleData;
    if (raw.data?.data?.fareRuleData !== undefined) return raw.data.data.fareRuleData;
    return raw.data;
  }

  return raw;
}

function formatFareRuleSuccess(data: FormattedFareRuleData): FormattedFareRuleData {
  return { success: true, isLoader: false, ...data };
}

function toDisplayLabel(key: string): string {
  return key
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

function toTemplateKey(typeValue: string): string {
  return (
    (typeValue || 'other')
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '_')
      .replace(/^_+|_+$/g, '') || 'other'
  );
}

export function unwrapArrayWrappedFareRule(source: any): any {
  if (isFareRuleArrayObjectFormat(source)) return source;
  if (Array.isArray(source) && source.length > 0) {
    return source[0];
  }
  return source;
}

export function unwrapFareRule(raw: any): any {
  if (raw == null) return raw;
  if (typeof raw === 'string') return raw;
  if (typeof raw !== 'object') return raw;
  if (raw.fareRule !== undefined && raw.fareRule !== null) {
    return raw.fareRule;
  }
  return raw;
}

export function isFareRuleTabRowArray(value: any): boolean {
  return (
    Array.isArray(value) &&
    value.length > 0 &&
    value.some(
      (row) =>
        row != null &&
        typeof row === 'object' &&
        !Array.isArray(row) &&
        'time' in row,
    )
  );
}

export function isFareRuleArrayObjectFormat(fareRule: any): boolean {
  if (!Array.isArray(fareRule) || fareRule.length === 0) return false;

  const first = fareRule[0];
  if (first == null || typeof first !== 'object' || Array.isArray(first)) return false;

  return Object.values(first).some(isFareRuleTabRowArray);
}

export function hasListBodyTabFormat(data: any): boolean {
  if (!data || typeof data !== 'object' || Array.isArray(data)) return false;
  return !!(
    data.cancellation ||
    data.refund ||
    data.reschedule ||
    data.dateChange ||
    data.noShow ||
    data.seatChange ||
    data.baggage
  );
}

export function buildListHeaderFromListBody(
  listBody: Record<string, any[]>,
  fallbackHeaders: FareRuleListHeader[] = DEFAULT_LIST_HEADER,
): FareRuleListHeader[] {
  const hasContent = (key: string) =>
    Array.isArray(listBody?.[key]) && listBody[key].length > 0;

  const matched = fallbackHeaders.filter((h) => hasContent(h.template));
  if (matched.length > 0) return matched;

  return Object.keys(listBody || {})
    .filter((key) => hasContent(key))
    .map((key) => ({
      template: key,
      header_name: toDisplayLabel(key),
    }));
}

export function extractListBodyTabs(source: any): Record<string, any[]> | null {
  if (source == null) return null;

  if (Array.isArray(source)) {
    if (
      source.length === 1 &&
      typeof source[0] === 'object' &&
      source[0] !== null &&
      !Array.isArray(source[0])
    ) {
      return extractListBodyTabs(source[0]);
    }
    return null;
  }

  if (typeof source !== 'object') return null;
  if (hasListBodyTabFormat(source)) return source;
  if (source.list_body && hasListBodyTabFormat(source.list_body)) {
    return source.list_body;
  }
  return null;
}

export function resolveActiveFareRuleTab(
  data: { list_header?: FareRuleListHeader[]; list_body?: Record<string, any> },
  fallback = 'cancellation',
): string {
  const listBody = data.list_body;
  if (!listBody) return fallback;

  const hasContent = (key: string) =>
    Array.isArray(listBody[key]) && listBody[key].length > 0;

  const fromHeader = data.list_header?.find((h) => hasContent(h.template))?.template;
  if (fromHeader) return fromHeader;

  const tabOrder = [
    'cancellation',
    'dateChange',
    'reschedule',
    'noShow',
    'seatChange',
    'baggage',
    'refund',
  ];
  for (const key of tabOrder) {
    if (hasContent(key)) return key;
  }

  const firstKey = Object.keys(listBody).find((key) => hasContent(key));
  return firstKey ?? fallback;
}

export function hasFareRuleContent(raw: any): boolean {
  if (!raw) return false;
  if (typeof raw === 'string') return raw.trim().length > 0;
  if (typeof raw === 'object') return Object.keys(raw).length > 0;
  return false;
}

export function extractHtmlFareRuleData(source: any): any | null {
  if (!source) return null;

  if (typeof source === 'string') {
    return source.trim() ? source : null;
  }

  if (typeof source !== 'object') return null;

  if (typeof source.fareRule === 'string' && source.fareRule.trim()) {
    return source;
  }

  if (Array.isArray(source.rules) && source.rules.length > 0) {
    return source;
  }

  if (source.miniFareRule?.Rules?.length > 0) {
    return source;
  }

  if (source.customFareRule) {
    return source;
  }

  if (source.data !== undefined) {
    const nested = extractHtmlFareRuleData(source.data);
    if (nested) return nested;
  }

  if (typeof source.html === 'string' && source.html.trim()) {
    return source.html;
  }

  return null;
}

export function formatHtmlFareRule(
  htmlSource: any,
  extra?: Partial<FormattedFareRuleData>,
): FormattedFareRuleData {
  const data = extractHtmlFareRuleData(htmlSource) ?? htmlSource;
  return {
    isLoader: false,
    html: { data },
    ...extra,
  };
}

export function flattenFareRuleObject(data: any): Record<string, any> {
  const result: Record<string, any> = {};

  function flatten(obj: any, prefix = ''): void {
    if (!obj || typeof obj !== 'object') return;
    for (const key of Object.keys(obj)) {
      const newKey = prefix
        ? `${prefix.charAt(0).toUpperCase() + prefix.slice(1)} ${key}`
        : key;
      if (typeof obj[key] === 'object' && obj[key] !== null && !Array.isArray(obj[key])) {
        flatten(obj[key], newKey);
      } else {
        result[newKey] = obj[key];
      }
    }
  }

  flatten(data);
  return result;
}

export function isHtmlString(value: unknown): boolean {
  return typeof value === 'string' && /<[a-z][\s\S]*>/i.test(value.trim());
}

export function extractStructuredFareRules(source: any): any[] | undefined {
  if (!source) return undefined;

  const direct =
    source?.StructuredFareRules ??
    source?.FareRuleListResponse?.FareRule?.[0]?.StructuredFareRules ??
    source?.fareRule?.FareRuleListResponse?.FareRule?.[0]?.StructuredFareRules;

  return Array.isArray(direct) && direct.length > 0 ? direct : undefined;
}

export function buildCustomFareRuleHeaders(finalData: any): FareRuleListHeader[] {
  const headers: FareRuleListHeader[] = [];

  if (finalData?.cancellation?.length > 0) {
    headers.push({ template: 'cancellation', header_name: 'Cancellation fee' });
  }
  if (finalData?.reschedule?.length > 0) {
    headers.push({ template: 'reschedule', header_name: 'Date change fee ' });
  }
  if (finalData?.noShow?.length > 0) {
    headers.push({ template: 'noShow', header_name: 'No show' });
  }
  if (finalData?.baggage?.length > 0) {
    headers.push({ template: 'baggage', header_name: 'Baggage' });
  }

  if (headers.length === 0) {
    headers.push({ template: 'cancellation', header_name: 'Cancellation fee' });
  }

  return headers;
}

export function formatCustomFareRule(finalData: any): FormattedFareRuleData {
  const list_header = buildCustomFareRuleHeaders(finalData);
  return {
    list_header,
    list_body: finalData,
    additionalInfo: finalData?.additionalInfo,
    activeFareRuleTab: list_header[0].template,
    isLoader: false,
  };
}

export function formatMiniFareRule(miniFareRule: any): FormattedFareRuleData | null {
  if (!miniFareRule || !Array.isArray(miniFareRule?.Rules)) {
    return null;
  }

  const currency = miniFareRule?.Currency || '';
  const list_body: Record<string, any[]> = {};
  const typeOrder: { template: string; display: string }[] = [];

  for (const rule of miniFareRule.Rules) {
    const fromDuration = rule?.FromDuration ?? '';
    const toDuration = rule?.ToDuration ? ` to ${rule.ToDuration}` : ' onwards';
    const departureType = rule?.DepartureType || '';
    const typeValue = rule?.Type ?? 'Other';
    const typeDisplay =
      typeof typeValue === 'string' && typeValue.trim()
        ? typeValue.trim()
        : String(typeValue);
    const template = toTemplateKey(typeDisplay);

    const penaltyContent =
      Array.isArray(rule?.PaxPenalties) && rule.PaxPenalties.length
        ? rule.PaxPenalties.map((pax: any) => ({
            head: pax?.PassengerType || '',
            value: `${currency} ${pax?.AirlineFee ?? '-'}`.trim(),
          }))
        : [{ head: '', value: 'No penalty data' }];

    if (!list_body[template]) {
      list_body[template] = [];
      typeOrder.push({ template, display: typeDisplay });
    }

    list_body[template].push({
      time: `${fromDuration}${toDuration}`,
      departureType,
      content: [{ head: 'Passenger Type', value: '' }, ...penaltyContent],
    });
  }

  if (!typeOrder.length) return null;

  const list_header = typeOrder.map((type) => ({
    header_name: type.display,
    head: `${type.template}_charges`,
    value: `${type.template}_charges_val`,
    template: type.template,
  }));

  return {
    list_header,
    list_body,
    activeFareRuleTab: typeOrder[0].template,
    isLoader: false,
  };
}

export function formatFareRuleLoading(): FormattedFareRuleData {
  return { isLoader: true };
}

export function formatFareRuleError(message?: string): FormattedFareRuleData {
  return {
    success: false,
    isLoader: false,
    html: { type: 'error' },
    message: message || DEFAULT_FARE_RULE_ERROR_MESSAGE,
  };
}

export function formatEmbeddedFareRule(
  rawFareRule: any,
  options?: { staticDisplay?: boolean; titleCaseFn?: (key: string) => string },
): EmbeddedFareRuleResult {
  if (!hasFareRuleContent(rawFareRule)) return null;

  if (typeof rawFareRule === 'string') {
    return {
      kind: 'component',
      data: formatHtmlFareRule(rawFareRule),
    };
  }

  if (Array.isArray(rawFareRule) && rawFareRule.length > 0) {
    if (isFareRuleArrayObjectFormat(rawFareRule)) {
      return {
        kind: 'component',
        data: { fare_rule: rawFareRule, isLoader: false },
      };
    }
    return formatEmbeddedFareRule(rawFareRule[0], options);
  }

  if (rawFareRule?.miniFareRule?.Rules?.length) {
    const mini = formatMiniFareRule(rawFareRule.miniFareRule);
    if (mini) return { kind: 'component', data: mini };
  }

  const finalData = unwrapFareRule(rawFareRule);

  if (typeof finalData === 'string') {
    return {
      kind: 'component',
      data: formatHtmlFareRule(finalData),
    };
  }

  if (finalData?.miniFareRule?.Rules?.length) {
    const mini = formatMiniFareRule(finalData.miniFareRule);
    if (mini) return { kind: 'component', data: mini };
  }

  if (finalData?.customFareRule) {
    return { kind: 'component', data: formatCustomFareRule(finalData) };
  }

  if (hasListBodyTabFormat(finalData)) {
    const list_header = buildListHeaderFromListBody(finalData);
    return {
      kind: 'component',
      data: {
        list_header,
        list_body: finalData,
        isLoader: false,
        activeFareRuleTab: resolveActiveFareRuleTab({
          list_header,
          list_body: finalData,
        }),
      },
    };
  }

  if (isFareRuleArrayObjectFormat(finalData)) {
    return {
      kind: 'component',
      data: { fare_rule: finalData, isLoader: false },
    };
  }

  const htmlData = extractHtmlFareRuleData(finalData);
  if (htmlData) {
    return {
      kind: 'component',
      data: formatHtmlFareRule(htmlData),
    };
  }

  const normalized = flattenFareRuleObject(finalData);

  if (options?.staticDisplay) {
    const titleCaseFn = options.titleCaseFn ?? toDisplayLabel;

    return {
      kind: 'static',
      data: {
        body: [normalized],
        header: Object.keys(normalized).map((key) => ({
          label: titleCaseFn(key),
          template: key,
        })),
      },
    };
  }

  return {
    kind: 'component',
    data: {
      fare_rule: normalized,
      isLoader: false,
    },
  };
}

export function formatFetchFareRuleResponse(
  apiResponse: any,
  options?: { includeListHeader?: boolean },
): FormattedFareRuleData {
  const errorMessage = getFareRuleErrorMessage(apiResponse);
  if (errorMessage) return formatFareRuleError(errorMessage);

  const apiData = normalizeFareRuleEnvelope(apiResponse);
  if (isEmptyFareRulePayload(apiData)) return formatFareRuleError();

  if (apiData?.miniFareRule?.Rules?.length) {
    const mini = formatMiniFareRule(apiData.miniFareRule);
    if (mini) return formatFareRuleSuccess({ ...mini, data: apiData });
  }

  const unwrappedData = unwrapFareRule(unwrapArrayWrappedFareRule(apiData));

  if (unwrappedData?.miniFareRule?.Rules?.length) {
    const mini = formatMiniFareRule(unwrappedData.miniFareRule);
    if (mini) return formatFareRuleSuccess({ ...mini, data: apiData });
  }

  if (unwrappedData?.customFareRule) {
    return formatFareRuleSuccess(formatCustomFareRule(unwrappedData));
  }

  const StructuredFareRules = extractStructuredFareRules(apiData);
  const listBodyTabs = extractListBodyTabs(apiData);
  const list_header = listBodyTabs
    ? apiData?.list_header?.length
      ? apiData.list_header
      : buildListHeaderFromListBody(listBodyTabs)
    : options?.includeListHeader !== false
      ? apiData?.list_header ?? DEFAULT_LIST_HEADER
      : undefined;

  const sharedFields: Partial<FormattedFareRuleData> = {
    data: apiData,
    list_body: listBodyTabs ?? apiData?.list_body ?? [],
    ...(list_header?.length ? { list_header } : {}),
  };

  if (StructuredFareRules?.length) {
    return formatFareRuleSuccess({
      html: { data: apiData },
      arrayData: StructuredFareRules,
      ...sharedFields,
    });
  }

  if (listBodyTabs) {
    const activeFareRuleTab = resolveActiveFareRuleTab({
      list_header: list_header ?? DEFAULT_LIST_HEADER,
      list_body: listBodyTabs,
    });
    return formatFareRuleSuccess({
      list_body: listBodyTabs,
      activeFareRuleTab,
      ...sharedFields,
    });
  }

  if (isFareRuleArrayObjectFormat(apiData)) {
    return formatFareRuleSuccess({
      fare_rule: apiData,
      ...sharedFields,
    });
  }

  const unwrappedListBodyTabs = extractListBodyTabs(unwrappedData);
  if (unwrappedListBodyTabs) {
    const unwrappedListHeader = buildListHeaderFromListBody(unwrappedListBodyTabs);
    return formatFareRuleSuccess({
      list_body: unwrappedListBodyTabs,
      list_header: unwrappedListHeader,
      activeFareRuleTab: resolveActiveFareRuleTab({
        list_header: unwrappedListHeader,
        list_body: unwrappedListBodyTabs,
      }),
      data: apiData,
    });
  }

  const htmlData =
    extractHtmlFareRuleData(apiData) ?? extractHtmlFareRuleData(unwrappedData);
  if (htmlData) {
    return formatFareRuleSuccess(formatHtmlFareRule(htmlData, sharedFields));
  }

  if (unwrappedData && typeof unwrappedData === 'object' && !Array.isArray(unwrappedData)) {
    return formatFareRuleSuccess({
      fare_rule: flattenFareRuleObject(unwrappedData),
      data: apiData,
    });
  }

  return formatFareRuleError();
}

export function formatSessionStorageFareRule(fareRule: any): FormattedFareRuleData | null {
  const errorMessage = getFareRuleErrorMessage(fareRule);
  if (errorMessage) return formatFareRuleError(errorMessage);

  const normalizedFareRule = normalizeFareRuleEnvelope(fareRule);
  if (!hasFareRuleContent(normalizedFareRule)) return null;

  const StructuredFareRules = extractStructuredFareRules(normalizedFareRule);
  if (StructuredFareRules) {
    return formatFareRuleSuccess({ arrayData: StructuredFareRules });
  }

  if (normalizedFareRule?.miniFareRule?.Rules?.length) {
    const mini = formatMiniFareRule(normalizedFareRule.miniFareRule);
    if (mini) return formatFareRuleSuccess(mini);
  }

  const finalData = unwrapFareRule(unwrapArrayWrappedFareRule(normalizedFareRule));
  if (finalData?.miniFareRule?.Rules?.length) {
    const mini = formatMiniFareRule(finalData.miniFareRule);
    if (mini) return formatFareRuleSuccess(mini);
  }

  const htmlData =
    extractHtmlFareRuleData(normalizedFareRule) ??
    extractHtmlFareRuleData(unwrapArrayWrappedFareRule(normalizedFareRule));
  if (htmlData) {
    return formatFareRuleSuccess(formatHtmlFareRule(htmlData));
  }

  const listBodyTabs = extractListBodyTabs(normalizedFareRule);
  if (listBodyTabs) {
    const list_header = buildListHeaderFromListBody(listBodyTabs);
    return formatFareRuleSuccess({
      list_header,
      list_body: listBodyTabs,
      activeFareRuleTab: resolveActiveFareRuleTab({
        list_header,
        list_body: listBodyTabs,
      }),
    });
  }

  if (finalData?.customFareRule) {
    return formatFareRuleSuccess(formatCustomFareRule(finalData));
  }

  if (finalData && typeof finalData === 'object' && !Array.isArray(finalData)) {
    return formatFareRuleSuccess({ body: finalData });
  }

  return null;
}

export function formatGetFareRuleResponse(
  apiResponse: any,
  index: number,
): FormattedFareRuleData | null {
  const errorMessage = getFareRuleErrorMessage(apiResponse);
  if (errorMessage) return formatFareRuleError(errorMessage);

  const normalizedResponse = normalizeFareRuleEnvelope(apiResponse);
  if (isEmptyFareRulePayload(normalizedResponse)) return formatFareRuleError();

  const entries = Array.isArray(normalizedResponse)
    ? normalizedResponse
    : [normalizedResponse];

  const entry =
    entries[index] ??
    (entries.length === 1 && entries[0]?.providerCode === 'CT' ? entries[0] : undefined);
  if (!entry) return formatFareRuleError();

  const providerCode = entry.providerCode ?? entries[0]?.providerCode;
  const rawFareRule = entry.fareRule !== undefined ? entry.fareRule : entry;
  const finalData = unwrapFareRule(unwrapArrayWrappedFareRule(rawFareRule));

  if (isEmptyFareRulePayload(rawFareRule) || isEmptyFareRulePayload(finalData)) {
    return formatFareRuleError();
  }

  if (finalData?.miniFareRule?.Rules?.length) {
    const mini = formatMiniFareRule(finalData.miniFareRule);
    if (mini) return formatFareRuleSuccess(mini);
  }

  if (finalData?.customFareRule) {
    return formatFareRuleSuccess(formatCustomFareRule(finalData));
  }

  const paymentListBody = extractListBodyTabs(rawFareRule) ?? extractListBodyTabs(finalData);
  if (paymentListBody) {
    const list_header = buildListHeaderFromListBody(paymentListBody);
    return formatFareRuleSuccess({
      list_body: paymentListBody,
      list_header,
      activeFareRuleTab: resolveActiveFareRuleTab({
        list_header,
        list_body: paymentListBody,
      }),
    });
  }

  const StructuredFareRules =
    extractStructuredFareRules(rawFareRule) ?? extractStructuredFareRules(finalData);
  if (StructuredFareRules?.length) {
    return formatFareRuleSuccess({ arrayData: StructuredFareRules });
  }

  if (providerCode === 'TBO') {
    const mini = formatMiniFareRule(rawFareRule?.miniFareRule);
    return formatFareRuleSuccess(mini ?? { html: { data: rawFareRule } });
  }

  const htmlData = extractHtmlFareRuleData(rawFareRule) ?? extractHtmlFareRuleData(finalData);
  if (htmlData) {
    return formatFareRuleSuccess(formatHtmlFareRule(htmlData));
  }

  if (isHtmlString(rawFareRule)) {
    return formatFareRuleSuccess(formatHtmlFareRule(rawFareRule));
  }

  if (isFareRuleArrayObjectFormat(rawFareRule)) {
    return formatFareRuleSuccess({ fare_rule: rawFareRule });
  }

  if (Array.isArray(rawFareRule)) {
    return formatFareRuleSuccess({ body: rawFareRule[0] || {} });
  }

  if (finalData && typeof finalData === 'object') {
    return formatFareRuleSuccess({ fare_rule: flattenFareRuleObject(finalData) });
  }

  return formatFareRuleError();
}
